import { useState, useCallback, useRef, useEffect, useMemo } from 'react';

export const useSwipeNavigation = (totalItems: number) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const containerWidthRef = useRef(window.innerWidth);
  
  // Use refs for touch tracking to avoid re-renders during drag
  const touchStartRef = useRef<number | null>(null);
  const touchStartYRef = useRef<number | null>(null);
  const touchCurrentRef = useRef<number | null>(null);
  const touchCurrentYRef = useRef<number | null>(null);
  const isDraggingRef = useRef(false);
  const isHorizontalRef = useRef<boolean | null>(null);
  const rafRef = useRef<number | null>(null);
  const slideContainerRef = useRef<HTMLDivElement | null>(null);
  
  // Force re-render state for dragOffset (only updated via rAF)
  const [dragOffset, setDragOffset] = useState(0);

  useEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        containerWidthRef.current = containerRef.current.offsetWidth;
      }
    };
    updateContainerWidth();
    window.addEventListener('resize', updateContainerWidth);
    return () => window.removeEventListener('resize', updateContainerWidth);
  }, []);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (isTransitioning) return;
    touchStartRef.current = e.targetTouches[0].clientX;
    touchStartYRef.current = e.targetTouches[0].clientY;
    touchCurrentRef.current = null;
    touchCurrentYRef.current = null;
    isDraggingRef.current = false;
    isHorizontalRef.current = null;
  }, [isTransitioning]);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (isTransitioning || touchStartRef.current === null) return;
    
    const currentX = e.targetTouches[0].clientX;
    const currentY = e.targetTouches[0].clientY;
    touchCurrentRef.current = currentX;
    touchCurrentYRef.current = currentY;
    
    // Determine direction lock on first significant move
    if (isHorizontalRef.current === null) {
      const dx = Math.abs(currentX - touchStartRef.current);
      const dy = Math.abs(currentY - touchStartYRef.current!);
      if (dx > 5 || dy > 5) {
        isHorizontalRef.current = dx > dy;
      }
    }
    
    if (!isHorizontalRef.current) return;
    
    isDraggingRef.current = true;
    
    // Update drag offset via rAF for smooth 60fps tracking
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      if (touchStartRef.current === null || touchCurrentRef.current === null) return;
      let delta = touchCurrentRef.current - touchStartRef.current;
      const width = containerWidthRef.current;
      
      // Add rubber-band resistance at edges
      if ((activeIndex === 0 && delta > 0) || (activeIndex === totalItems - 1 && delta < 0)) {
        delta = delta * 0.3;
      }
      
      setDragOffset((delta / width) * 100);
    });
  }, [isTransitioning, activeIndex, totalItems]);

  const onTouchEnd = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    
    if (touchStartRef.current === null || !isDraggingRef.current) {
      touchStartRef.current = null;
      touchCurrentRef.current = null;
      setDragOffset(0);
      return;
    }
    
    const delta = (touchCurrentRef.current ?? touchStartRef.current) - touchStartRef.current;
    const width = containerWidthRef.current;
    const swipeRatio = Math.abs(delta) / width;
    const threshold = 0.15; // 15% of width triggers slide change
    
    setIsTransitioning(true);
    setDragOffset(0);
    
    if (swipeRatio > threshold) {
      if (delta < 0 && activeIndex < totalItems - 1) {
        setActiveIndex(prev => prev + 1);
      } else if (delta > 0 && activeIndex > 0) {
        setActiveIndex(prev => prev - 1);
      }
    }
    
    touchStartRef.current = null;
    touchCurrentRef.current = null;
    isDraggingRef.current = false;
    isHorizontalRef.current = null;
    
    setTimeout(() => setIsTransitioning(false), 400);
  }, [activeIndex, totalItems]);

  const goToIndex = useCallback((index: number) => {
    if (isTransitioning || index === activeIndex || index < 0 || index >= totalItems) return;
    setIsTransitioning(true);
    setActiveIndex(index);
    setTimeout(() => setIsTransitioning(false), 400);
  }, [activeIndex, isTransitioning, totalItems]);

  const isDragging = isDraggingRef.current && dragOffset !== 0;

  return {
    activeIndex,
    setActiveIndex: goToIndex,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    isTransitioning,
    dragOffsetPercent: dragOffset,
    isDragging,
    containerRef,
    slideContainerRef
  };
};
